import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import CardSection from '@shared/components/CardSection';
import { ApiRequestError } from '@api/client/apiClient';
import { useOperations } from '@features/updates/hooks/useOperations';

interface Props {
  selectedArtifactId: string | null;
  onSelect: (artifactId: string) => void;
}

export default function ArtifactSelector({ selectedArtifactId, onSelect }: Props) {
  const { data, isLoading, error, refetch } = useOperations();

  if (isLoading) return <PanelSkeleton />;

  if (error) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error.message ?? 'Failed to load artifacts'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  const artifacts = data?.items.filter((op) => op.status === 'succeeded' && op.artifactId) ?? [];

  if (artifacts.length === 0) {
    return (
      <CardSection>
        <Typography variant="body2" color="text.secondary">
          No artifacts available. Complete a successful update operation first.
        </Typography>
      </CardSection>
    );
  }

  return (
    <div data-testid="artifact-selector">
      <CardSection title="Select Artifact">
        <RadioGroup value={selectedArtifactId ?? ''} onChange={(e) => onSelect(e.target.value)}>
          {artifacts.map((op) => (
            <FormControlLabel
              key={op.artifactId}
              value={op.artifactId!}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  {op.type} — {op.operationId}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {op.finishedAtUtc
                      ? new Date(op.finishedAtUtc).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''}
                  </Typography>
                </Typography>
              }
            />
          ))}
        </RadioGroup>
      </CardSection>
    </div>
  );
}
